import React, { useState } from 'react';
import { Word, WordExample, WordStatus, updateWordStatus } from '../utils/api';
import { useTheme } from '../hooks/useTheme';
import CategorySelector from './CategorySelector';
import '../styles/WordCard.scss';
import alreadyLearnedIconLight from '../assets/light/alreadyLearnedIcon.svg';
import learningIconLight from '../assets/light/learningIcon.svg';
import blackListIconLight from '../assets/light/blackListIcon.svg';
import alreadyLearnedIconDark from '../assets/dark/alreadyLearnedIcon.svg';
import learningIconDark from '../assets/dark/learningIcon.svg';
import blackListIconDark from '../assets/dark/blackListIcon.svg';
import logger from '../utils/logger';

interface WordCardProps {
  word: Word;
  onClose: () => void;
  onAddToCategory?: () => void;
  categoryName?: string;
  isInCategory?: boolean;
  showStatusButtons?: boolean;
  onStatusChange?: (status: WordStatus) => void;
}

const WordCard: React.FC<WordCardProps> = ({ 
  word, 
  onClose, 
  onAddToCategory,
  categoryName,
  isInCategory = false,
  showStatusButtons = false,
  onStatusChange
}) => {
  const { theme } = useTheme();
  const [isCategorySelectorOpen, setIsCategorySelectorOpen] = useState(false);
  
  const handleAddClick = () => {
    if (onAddToCategory) {
      onAddToCategory();
    }
  };

  const handleStatusChange = async (status: WordStatus) => {
    try {
      // Для статуса "Учу" открываем селектор категорий
      if (status === 'IN_PROGRESS') {
        setIsCategorySelectorOpen(true);
        return;
      }
      
      const response = await updateWordStatus(word.word, status);
      if (response.success) {
        logger.log(`Статус слова "${word.word}" успешно изменен на ${status}`);
        if (onStatusChange) {
          onStatusChange(status);
        }
      } else {
        logger.error(`Ошибка при изменении статуса слова: ${response.error}`);
      }
    } catch (error) {
      logger.error('Исключение при изменении статуса слова:', error);
    }
  };

  const handleCategorySelected = async () => {
    // После выбора категории устанавливаем статус IN_PROGRESS
    try {
      const response = await updateWordStatus(word.word, 'IN_PROGRESS');
      if (response.success) {
        logger.log(`Статус слова "${word.word}" успешно изменен на IN_PROGRESS после выбора категории`);
        if (onStatusChange) {
          onStatusChange('IN_PROGRESS');
        }
      } else {
        logger.error(`Ошибка при изменении статуса слова: ${response.error}`);
      }
    } catch (error) {
      logger.error('Исключение при изменении статуса слова:', error);
    }
  };

  logger.log('Отображаю карточку слова:', word, 'уже в категории:', isInCategory, 'показывать статусы:', showStatusButtons);
  
  return (
    <>
      <div className="word-card-overlay" onClick={onClose}>
        <div className="word-card" onClick={(e) => e.stopPropagation()}>
          <div className="word-card-close" onClick={onClose}>×</div>
          <div className="word-card-header">
            <div className="word-character">{word.word}</div>
            <div className="word-pinyin">{word.pinyin || 'Нет пиньиня'}</div>
          </div>
          <div className="word-card-content">
            <div className="word-translation">{word.translation || 'Нет перевода'}</div>
            {word.example && (
              <div className="word-example">
                <div className="example-title">Пример:</div>
                <div className="example-sentence">{word.example.sentence}</div>
                <div className="example-pinyin">{word.example.pinyin}</div>
                <div className="example-translation">{word.example.translation}</div>
              </div>
            )}
          </div>
          <div className="word-card-footer">
            {!isInCategory && onAddToCategory && categoryName && (
              <button className="add-button" onClick={handleAddClick}>
                Добавить в категорию "{categoryName}"
              </button>
            )}
            
            {showStatusButtons && (
              <div className="status-buttons">
                <button 
                  className="status-button" 
                  onClick={() => handleStatusChange('LEARNED')}
                  title="Знаю"
                >
                  <img 
                    src={theme === 'light' ? alreadyLearnedIconLight : alreadyLearnedIconDark} 
                    alt="Знаю" 
                    className="status-icon"
                  />
                </button>
                <button 
                  className="status-button" 
                  onClick={() => handleStatusChange('IN_PROGRESS')}
                  title="Учу"
                >
                  <img 
                    src={theme === 'light' ? learningIconLight : learningIconDark} 
                    alt="Учу" 
                    className="status-icon"
                  />
                </button>
                <button 
                  className="status-button" 
                  onClick={() => handleStatusChange('BLACK_LIST')}
                  title="ЧС"
                >
                  <img 
                    src={theme === 'light' ? blackListIconLight : blackListIconDark} 
                    alt="ЧС" 
                    className="status-icon"
                  />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      
      <CategorySelector 
        isOpen={isCategorySelectorOpen}
        onClose={() => setIsCategorySelectorOpen(false)}
        word={word}
        onCategorySelected={handleCategorySelected}
      />
    </>
  );
};

export default WordCard; 